'use strict';

import './components/index';

import {
    UPDATE_LANGUAGE_EVENT,
    SEARCH_IMG_OBJECT_EVENT,
    SEARCH_IMG_RESULT_EVENT,
    SEARCH_REPORTS_RESULT_EVENT,
    SEARCH_REPORT_DETAILS_RESULT_EVENT,
} from '@core/constants/EVENTS';
import { TRIGGER_UPDATE_LIGHTBOX_NAME_EVENT } from '@core/constants/LIGHTBOX';
import {
    SEARCHER_TYPES,
    SEARCHER_OPTIONS,
    DEFAULT_ORDER_BY_OPTION,
} from '@core/constants/SEARCHER';
import { ROUTES } from '@core/constants/ROUTES';
import {
    globalSharedStyles,
    typographySharedStyles,
    imageIconsStyles,
    commonTabsLayoutStyles,
} from '@core/styles';
import {
    DATA_TRANSLATE_ATTRIBUTE,
    REPORTS_VIEW_ATTRIBUTE,
    REPORT_DETAILS_VIEW_ATTRIBUTE,
} from '@core/constants/ATTRIBUTES';
import { TOGGLE_TAB_PANES_EVENT } from './CONSTANTS';
import {
    fillDuration,
    getFillDurationFromStore,
    handleTranslationHelper,
    addRippleBehavior,
    handleReportDetailsResult,
    handleToggleTabPanes,
    configureActionButtons,
} from './utils';

import htmlContent from './TabsLayout.html';
import cssContent from './TabsLayout.css';

/**
 * TabsLayout component
 *
 * A web component that provides a tabbed layout for displaying search results.
 * It handles both images and reports views with proper state management and
 * event handling for tab switching and content updates.
 *
 * @class TabsLayout
 * @extends {HTMLElement}
 */
class TabsLayout extends HTMLElement {
    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        /** @type {HTMLTemplateElement} */
        const template = document.createElement('template');
        template.innerHTML = htmlContent;

        const styleSheet = new CSSStyleSheet();
        styleSheet.replaceSync(cssContent);

        this.shadowRoot.adoptedStyleSheets = [
            globalSharedStyles,
            typographySharedStyles,
            imageIconsStyles,
            commonTabsLayoutStyles,
            styleSheet,
        ];
        this.shadowRoot.appendChild(template.content.cloneNode(true));

        // Initialize properties
        this._reportsView = !!this.getAttribute(REPORTS_VIEW_ATTRIBUTE) || false;
        this._reportDetailsView =
            !!this.getAttribute(REPORT_DETAILS_VIEW_ATTRIBUTE) || false;
        this._autoUpdateReportsDefaultValue = false;
        this._currentReportType = '';
        this._customCurrentTitle = '';
        this._isLoading = false;
        this._defaultImgs = null;
        this._totalElements = 0;
        this._titleElement = '';
        this._pdfFileName = '';

        // Cache DOM references
        this._tabTitle = null;
        this._durationElement = null;
        this._headerActionButtons = null;

        // Bind event handlers
        this._handleTranslationHelperBound = this._handleTranslationHelper.bind(this);
        this._handleSearchImgResultBound = this._handleSearchImgResult.bind(this);
        this._handleSearchReportsResultBound = this._handleSearchReportsResult.bind(this);
        this._handleSearchReportDetailsResultBound =
            this._handleSearchReportDetailsResult.bind(this);
        this._handleTriggerUpdateLightboxNameBound =
            this._handleTriggerUpdateLightboxName.bind(this);
        this._handleToggleTabPanesBound = this._handleToggleTabPanes.bind(this);
        this._handleSearchImgObjectBound = this._handleSearchImgObject.bind(this);
    }

    /**
     * Called when the element is connected to the DOM
     * @returns {void}
     */
    connectedCallback() {
        this._checkCurrentSearcherObj();
        this._cacheDOMReferences();
        this.preRender();
        this._addEventListeners();
        this._handleActionButtons();
        this._addRippleBehavior();
    }

    /**
     * Called when the element is disconnected from the DOM
     * @returns {void}
     */
    disconnectedCallback() {
        this._removeEventListeners();
    }

    /**
     * Cache DOM references for better performance
     * @returns {void}
     * @private
     */
    _cacheDOMReferences() {
        this._tabTitle = this.shadowRoot.querySelector('[data-tab-title]');
        this._durationElement = this.shadowRoot.querySelector('[data-amount-images]');
        this._headerActionButtons = this.shadowRoot.querySelector(
            '[data-header-action-buttons-content]',
        );
    }

    /**
     * Check current searcher object
     * @returns {void}
     * @private
     */
    _checkCurrentSearcherObj() {
        // Implementation would go here if needed
    }

    /**
     * Pre-render the component elements
     * @param {string} [reportType] - The title to show in the tab. Default is empty.
     * @param {string} [customTitle] - The title to show in the tab. Default is empty.
     * @returns {void}
     */
    preRender(reportType = '') {
        if (!this._tabTitle) return;

        const urlParams = new URLSearchParams(window.location.search);

        if (this._reportsView) {
            const orderByContainers = this.shadowRoot.querySelectorAll(
                '[data-order-by-select]',
            );

            if (this._reportDetailsView) {
                this._tabTitle.removeAttribute(DATA_TRANSLATE_ATTRIBUTE);

                if (this._customCurrentTitle && !this._tabTitle.querySelector('span')) {
                    this._tabTitle.innerHTML = '';
                    const spanTitle = document.createElement('span');
                    spanTitle.innerHTML = this._customCurrentTitle;

                    if (urlParams.has(SEARCHER_TYPES.LIGHTBOX_ID)) {
                        const spanDefault = document.createElement('span');
                        spanDefault.setAttribute(
                            DATA_TRANSLATE_ATTRIBUTE,
                            `results.titles.${
                                urlParams.has(SEARCHER_TYPES.LIGHTBOX_ID)
                                    ? 'lightbox'
                                    : 'report'
                            }`,
                        );

                        this._tabTitle.appendChild(spanDefault);
                    }
                    this._tabTitle.appendChild(spanTitle);
                }

                this._tabTitle.style.display = 'flex';
                this._tabTitle.style.gap = '5px';
                this._tabTitle.style.alignItems = 'center';
                this._tabTitle.style.viewTransitionName = `report-title-${urlParams.get(SEARCHER_TYPES.LIGHTBOX_ID) || urlParams.get(SEARCHER_TYPES.REPORT_ID)}`;
            } else {
                if (orderByContainers.length) {
                    orderByContainers.forEach((container) => {
                        container.remove();
                    });
                }

                this._tabTitle.setAttribute(
                    DATA_TRANSLATE_ATTRIBUTE,
                    `results.titles.${reportType || 'default'}`,
                );
            }

            if (window.location.pathname.includes(ROUTES.REPORTS_LIST)) {
                this._tabTitle.style.marginBottom = '1em';
            } else {
                const defaultHomeTooltip = this.shadowRoot.querySelector(
                    '[data-default-home-tooltip]',
                );
                if (defaultHomeTooltip) {
                    defaultHomeTooltip.remove();
                }
                this._tabTitle.style.marginBottom = '0';
            }

            // Hide unnecessary elements for Reports List view
            if (urlParams.has(SEARCHER_TYPES.LIGHTBOX_ID)) {
                const updateReports = this.shadowRoot.querySelectorAll(
                    '[data-update-reports]',
                );
                if (updateReports.length)
                    updateReports.forEach((updateReport) => {
                        updateReport.remove();
                    });
            }

            const reportsTab = this.shadowRoot.querySelector('#reports');
            if (reportsTab) reportsTab.remove();

            const tabButtons = this.shadowRoot.querySelector('[data-tab-buttons]');
            if (tabButtons) tabButtons.remove();

            const tabHeaderTitle = this.shadowRoot.querySelector('.tab-header-title');
            if (tabHeaderTitle) {
                tabHeaderTitle.style.width = '66%';
            }
        } else {
            const updateReports = this.shadowRoot.querySelectorAll(
                '[data-update-reports]',
            );
            if (updateReports.length)
                updateReports.forEach((updateReport) => {
                    updateReport.remove();
                });

            const searchOption =
                urlParams.get(SEARCHER_TYPES.OPTION) || SEARCHER_OPTIONS.ED_IMGS;
            const orderBySelects = this.shadowRoot.querySelectorAll(
                '[data-order-by-select]',
            );

            this._tabTitle.setAttribute(
                DATA_TRANSLATE_ATTRIBUTE,
                `results.titles.${searchOption}.${
                    orderBySelects[0]?.orderByOption || DEFAULT_ORDER_BY_OPTION
                }`,
            );
        }
    }

    /**
     * Handle translation helper
     * @returns {void}
     * @private
     */
    _handleTranslationHelper() {
        handleTranslationHelper(this.shadowRoot);
    }

    /**
     * Add event listeners to the component
     * @returns {void}
     * @private
     */
    _addEventListeners() {
        document.addEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );

        this._addEventListenerTabs();

        document.addEventListener(
            SEARCH_IMG_RESULT_EVENT,
            this._handleSearchImgResultBound,
        );
        document.addEventListener(
            SEARCH_REPORTS_RESULT_EVENT,
            this._handleSearchReportsResultBound,
        );
        document.addEventListener(
            SEARCH_REPORT_DETAILS_RESULT_EVENT,
            this._handleSearchReportDetailsResultBound,
        );
        document.addEventListener(
            TRIGGER_UPDATE_LIGHTBOX_NAME_EVENT,
            this._handleTriggerUpdateLightboxNameBound,
        );
        document.addEventListener(
            TOGGLE_TAB_PANES_EVENT,
            this._handleToggleTabPanesBound,
        );
    }

    /**
     * Remove event listeners to prevent memory leaks
     * @returns {void}
     * @private
     */
    _removeEventListeners() {
        document.removeEventListener(
            UPDATE_LANGUAGE_EVENT,
            this._handleTranslationHelperBound,
        );
        document.removeEventListener(
            SEARCH_IMG_RESULT_EVENT,
            this._handleSearchImgResultBound,
        );
        document.removeEventListener(
            SEARCH_REPORTS_RESULT_EVENT,
            this._handleSearchReportsResultBound,
        );
        document.removeEventListener(
            SEARCH_REPORT_DETAILS_RESULT_EVENT,
            this._handleSearchReportDetailsResultBound,
        );
        document.removeEventListener(
            TRIGGER_UPDATE_LIGHTBOX_NAME_EVENT,
            this._handleTriggerUpdateLightboxNameBound,
        );
        document.removeEventListener(
            TOGGLE_TAB_PANES_EVENT,
            this._handleToggleTabPanesBound,
        );
        document.removeEventListener(
            SEARCH_IMG_OBJECT_EVENT,
            this._handleSearchImgObjectBound,
        );
    }

    /**
     * Add event listener to the tabs
     * @returns {void}
     * @private
     */
    _addEventListenerTabs() {
        document.addEventListener(
            SEARCH_IMG_OBJECT_EVENT,
            this._handleSearchImgObjectBound,
        );
    }

    /**
     * Handle search image result event
     * @param {CustomEvent} event - The search image result event
     * @returns {void}
     * @private
     */
    _handleSearchImgResult(event) {
        if (event.detail.page === 0) {
            fillDuration(this._durationElement, event.detail);
        } else {
            getFillDurationFromStore(this._durationElement);
        }

        this._handleTranslationHelper();
    }

    /**
     * Handle search reports result event
     * @param {CustomEvent} event - The search reports result event
     * @returns {void}
     * @private
     */
    _handleSearchReportsResult(event) {
        this._currentReportType = event.detail.reportFilter;

        if (event.detail.page === 0) {
            fillDuration(this._durationElement, event.detail);
        } else {
            getFillDurationFromStore(this._durationElement);
        }

        this.preRender(this._currentReportType);
        this._handleTranslationHelper();
    }

    /**
     * Handle search report details result event
     * @param {CustomEvent} event - The search report details result event
     * @returns {void}
     * @private
     */
    _handleSearchReportDetailsResult(event) {
        handleReportDetailsResult(event, this);
    }

    /**
     * Handle trigger update lightbox name event
     * @param {CustomEvent} event - The trigger update lightbox name event
     * @returns {void}
     * @private
     */
    _handleTriggerUpdateLightboxName(event) {
        const spanTabTitle = this.shadowRoot.querySelector(
            '[data-tab-title] span:last-child',
        );
        if (!spanTabTitle) return;

        const total = spanTabTitle.innerHTML.split('(')[1];
        spanTabTitle.innerHTML = `${event.detail.name} (${total}`;
    }

    /**
     * Handle toggle tab panes event
     * @param {CustomEvent} event - The toggle tab panes event
     * @returns {void}
     * @private
     */
    _handleToggleTabPanes(event) {
        handleToggleTabPanes(event, this.shadowRoot);
    }

    /**
     * Handle search image object event
     * @returns {void}
     * @private
     */
    _handleSearchImgObject() {
        this._triggerAllTranslations();
    }

    /**
     * Trigger all translations
     * @returns {void}
     * @private
     */
    _triggerAllTranslations() {
        this.preRender();
        this._handleTranslationHelper();
    }

    /**
     * Add ripple behavior to elements
     * @returns {void}
     * @private
     */
    _addRippleBehavior() {
        addRippleBehavior(this.shadowRoot);
    }

    /**
     * Handle action buttons configuration
     * @returns {void}
     * @private
     */
    _handleActionButtons() {
        configureActionButtons(this.shadowRoot, this);
    }
}

customElements.define('tabs-layout', TabsLayout);
